import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  shortcut?: string;
  variant?: "default" | "destructive";
  disabled?: boolean;
  onSelect: () => void;
}

export type ContextMenuSection = ContextMenuItem | { type: "separator" };

interface ContextMenuProps {
  items: ContextMenuSection[];
  children: React.ReactNode;
  className?: string;
}

interface MenuPos { x: number; y: number }

export function ContextMenu({ items, children, className }: ContextMenuProps) {
  const [pos, setPos] = useState<MenuPos | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setPos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (!pos) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pos, close]);

  // Adjust position to stay in viewport after render
  useEffect(() => {
    if (!pos || !menuRef.current) return;
    const menu = menuRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = menu.getBoundingClientRect();
    let { x, y } = pos;
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (y + rect.height > vh - 8) y = vh - rect.height - 8;
    if (x !== pos.x || y !== pos.y) setPos({ x, y });
  }, [pos]);

  return (
    <>
      <div className={cn("context-menu-trigger", className)} onContextMenu={handleContextMenu}>
        {children}
      </div>
      {pos &&
        createPortal(
          <div
            ref={menuRef}
            className="context-menu"
            style={{ left: pos.x, top: pos.y }}
            role="menu"
          >
            {items.map((item, i) => {
              if ("type" in item && item.type === "separator") {
                return <div key={`sep-${i}`} className="context-menu-separator" role="separator" />;
              }
              const menuItem = item as ContextMenuItem;
              return (
                <button
                  key={menuItem.id}
                  className={cn(
                    "context-menu-item",
                    menuItem.variant === "destructive" && "context-menu-item-destructive"
                  )}
                  disabled={menuItem.disabled}
                  role="menuitem"
                  onClick={() => { menuItem.onSelect(); close(); }}
                >
                  {menuItem.icon && <span className="context-menu-item-icon">{menuItem.icon}</span>}
                  <span className="context-menu-item-label">{menuItem.label}</span>
                  {menuItem.shortcut && <kbd className="context-menu-shortcut">{menuItem.shortcut}</kbd>}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
